import { useTranslation } from 'react-i18next';
import { useModuleStrings } from '@platform/modularity/useModuleStrings';
import { cx } from '@platform/core/lib/cx';
import { isFieldRestricted } from '../fields/fieldRestricted';
import { FieldRenderer } from '../fields/FieldRenderer';
import { fieldPlacementOf } from './fieldPlacement';
import { fieldVisible } from './fieldVisible';
import type { IFieldGroupProps } from './IFieldGroupProps';
import { valueOf } from './valueOf';

/**
 * Група полів (UIUX forms/groups, field-grid-and-widths): заголовок 12/700 ВЕЛИКИМИ акцентом (рядки модуля → стандартна група
 * платформи → визначення), поля на сітці одиниць 150px у порядку визначення; ширина S/M/L і власний рядок — з розкладки групи
 * (`layout`), інакше за типом поля; група `full` займає весь рядок полотна. Поле з умовою показу (`visibleWhen`) — лише за відповідного
 * значення поля-умови (група без жодного видимого поля прихована). Дати в групах — без позначки простроченого терміну
 * (прототип v4: червоний лише в ключовому рядку), тому контрол дати не отримує запис-контекст. Поле з персональними даними, приховане
 * сервером, — без значення й без редагування (isFieldRestricted).
 */
export function FieldGroup({ ws, rt, group, record, editor, readOnly }: IFieldGroupProps) {
  const { t } = useTranslation();
  const strings = useModuleStrings();
  const standard = group.title.en ? t(`form.groups.${group.title.en}`, { defaultValue: '' }) : '';
  const title = strings.formGroup(rt.key, group.id, standard || group.title);
  const fields = group.fields.map((k) => rt.fields.find((f) => f.key === k)).filter((f): f is NonNullable<typeof f> => Boolean(f))
    .filter((f) => fieldVisible(f, (key) => valueOf(record, editor.draft, key)));
  if (!fields.length) return null;
  return (
    <section className={cx('group', group.full && 'group--full')} aria-label={title}>
      <h3 className="group__title">{title}</h3>
      <div className="group__grid">
        {fields.map((f) => {
          const place = fieldPlacementOf(group, f);
          return (
            <FieldRenderer key={f.key} field={f} rt={rt} ws={ws} record={f.type === 'date' ? undefined : record} width={place.width} ownLine={place.ownLine}
              value={valueOf(record, editor.draft, f.key)} onChange={editor.setField} readOnly={readOnly} error={editor.fieldErrors[f.key]}
              restricted={isFieldRestricted(record, f)} />
          );
        })}
      </div>
    </section>
  );
}
